import { Box, Heading, HStack, LinkBox, LinkOverlay } from "@chakra-ui/react"
import { useScrollApp } from "@pathwright/utils/src/scroll/useScroll"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import Link from "@pathwright/ux/src/components/link/Link"
import { motion, useMotionValueEvent, useTransform } from "framer-motion"
import { useRef, useState } from "react"
import { useIntersection } from "react-use"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import HomeBackground from "../components/ui/HomeBackground"

const MotionBox = motion(Box)

const HomeHeader = () => {
  const { me } = usePathwrightContext()

  const targetRef = useRef<HTMLDivElement>(null)

  // We only enable the motion animation after the first scroll event.
  // This is due to a visual glitch when first mounting the component
  // due to a scale keframe animation applied to the parent.
  const [enableAnimation, setEnableAnimation] = useState(false)

  let { scrollY } = useScrollApp({
    target: targetRef,
    offset: ["start end", "end start"]
  })

  // Offset scroll animation by the top offset of the container.
  const intersection = useIntersection(targetRef, {
    root: null,
    threshold: 1
  })
  const topOffset = intersection?.boundingClientRect.top || 0

  // Animate size of the header based on scroll position.
  const scale = useTransform(
    scrollY,
    [0 + topOffset, 120 + topOffset],
    [1, 0.8]
  )

  // Only enabling the animation after the first scroll event.
  useMotionValueEvent(scrollY, "change", () => {
    if (!enableAnimation) setEnableAnimation(true)
  })

  const resetScrollPosition = () => {
    if (targetRef.current) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  return (
    <Box ref={targetRef} className="HomeHeader" w="100%" zIndex={10}>
      <Box
        pos="absolute"
        inset={0}
        // Hack to ensure bg spans the entire paths list so it covers everything in it.
        // This is necessary due to the augmentation layout. We span to the left and right
        // of the paths list to to cover enlarged paths when you hover. Sigh.
        left="-2"
        right="-2"
        sx={{
          maskImage:
            "linear-gradient(to top, transparent, rgba(0, 0, 0, .75) 30%, black);"
        }}
      >
        <HomeBackground />
      </Box>
      <MotionBox
        as={motion.div}
        w="100%"
        style={{ scale: enableAnimation ? scale : 1 }}
        px={4}
      >
        <LinkBox
          as={HStack}
          w="100%"
          justifyContent="center"
          pos="relative"
          spacing={3}
        >
          <LinkOverlay as={Link} to="/home?panel=/settings">
            <UserAvatar
              user={me}
              size={["md", "lg"]}
              onClick={resetScrollPosition}
              cursor="pointer"
              pointerEvents="auto"
            />
          </LinkOverlay>
          <Heading
            as="h1"
            className="theme-heading theme-heading-bold"
            pos="relative"
            fontSize={["1.5em", "2em"]}
            color="whiteAlpha.900"
            onClick={resetScrollPosition}
            cursor="pointer"
            pointerEvents="auto"
          >
            {me.full_name}
          </Heading>
        </LinkBox>
      </MotionBox>
    </Box>
  )
}

export default HomeHeader
